import { useQuery } from '@tanstack/react-query';
import { HeatmapChart, Card, EmptyState, Skeleton } from '@bbpos/ui';
import { dashboardService } from '@/services/dashboard.service';
import { useDashboardStore } from '@/stores/use-dashboard-store';

// Day-of-week labels matching Postgres EXTRACT(DOW): 0 = Sunday.
const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

// Hour labels: show every hour but only render the busiest band (6 AM – 11 PM)
// to keep the cell count readable. Bills outside this window are rare for
// retail/restaurant tenants.
const VISIBLE_HOURS = Array.from({ length: 18 }, (_, i) => i + 6); // 6..23

function hourLabel(h: number): string {
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  if (h < 12) return `${h} AM`;
  return `${h - 12} PM`;
}

/**
 * 7-day × 24-hour heatmap of bill counts. Replaces the previous single-day
 * heatmap that collapsed to a one-row strip. Cells are bucketed in IST.
 */
export function SalesHeatmapWidget() {
  const locationId = useDashboardStore((s) => s.selectedLocationId);

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'sales-heatmap', locationId],
    queryFn: () => dashboardService.getSalesHeatmap({ locationId, days: 7 }),
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <Card className="p-4 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80">
        <Skeleton variant="text" width="40%" height={16} />
        <div className="mt-3">
          <Skeleton variant="rect" height={200} />
        </div>
      </Card>
    );
  }

  const cells = data?.cells ?? [];
  if (cells.length === 0) {
    return (
      <Card className="p-4 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80">
        <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-2">
          Sales Heatmap · Last 7 days
        </h3>
        <EmptyState
          variant="inline"
          title="No bills in the last 7 days"
          description="Complete a few sales to see your busiest weekday-hour cells"
        />
      </Card>
    );
  }

  // Materialise the 7×18 grid so HeatmapChart gets a row per (day, visible hour)
  // even when the backend didn't return one. Empty cells render as the lowest
  // intensity bucket.
  const heatmapData = DOW_LABELS.flatMap((dayLabel, dow) =>
    VISIBLE_HOURS.map((hour) => {
      const cell = cells.find((c) => c.day_of_week === dow && c.hour === hour);
      return {
        row: dayLabel,
        col: hourLabel(hour),
        value: cell ? cell.bill_count : 0,
      };
    }),
  );

  return (
    <HeatmapChart
      data={heatmapData}
      title={`Sales Heatmap · Last ${data?.range.days ?? 7} days`}
      rowLabel="Day"
      colLabel="Hour"
      className="shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80 p-4"
    />
  );
}
